import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";

const customModalStyles = {
  content: {
    padding: "20px",
    width: "80%",
    maxWidth: "600px",
    margin: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
};

const fieldStyle = {
  width: "100%",
  padding: "8px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid #ccc",
};

const labelStyle = {
  display: "block",
  fontWeight: "bold",
  marginBottom: "5px",
};

const emptyForm = {
  batchId: "",
  name: "",
  slug: "",
  description: "",
  order: "",
  isActive: true,
};

const SdcPrograms = () => {
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchFilter, setBatchFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchBatches();
  }, []);

  useEffect(() => {
    fetchItems();
  }, [batchFilter]);

  const fetchBatches = async () => {
    try {
      const res = await axiosInstance.get("sdc/batches/all");
      setBatches(res.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params =
        batchFilter !== "all" ? { batchId: batchFilter } : undefined;
      const res = await axiosInstance.get("sdc/programs/all", { params });
      setItems(res.data?.data || []);
    } catch (error) {
      Swal.fire("Error", "Failed to load programs", "error");
    }
    setLoading(false);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...emptyForm,
      batchId: batchFilter !== "all" ? batchFilter : "",
    });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingId(item._id);
    setForm({
      batchId: item.batchId || item.batch?._id || "",
      name: item.name || "",
      slug: item.slug || "",
      description: item.description || "",
      order: item.order ?? "",
      isActive: item.isActive !== false,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.batchId) {
      Swal.fire("Missing batch", "Select a batch", "warning");
      return;
    }
    if (!form.name.trim()) {
      Swal.fire("Missing name", "Program name is required", "warning");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        batchId: form.batchId,
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        description: form.description,
        isActive: form.isActive,
      };
      if (form.order !== "" && form.order !== null) {
        payload.order = Number(form.order);
      }

      if (editingId) {
        await axiosInstance.put(`sdc/programs/change/${editingId}`, payload);
        Swal.fire("Saved", "Program updated", "success");
      } else {
        await axiosInstance.post("sdc/programs/create", payload);
        Swal.fire("Saved", "Program created", "success");
      }
      setShowModal(false);
      fetchItems();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to save program",
        "error"
      );
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this program?",
      text: "Courses linked to it will be unlinked.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`sdc/programs/delete/${id}`);
      fetchItems();
      Swal.fire("Deleted", "Program removed", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete program",
        "error"
      );
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0 }}>SDC Programs</h2>
          <p style={{ margin: "6px 0 0", color: "#666" }}>
            Programs belong to a batch. Add courses under each program next.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          style={{
            padding: "10px 20px",
            backgroundColor: "#4CAF50",
            color: "#fff",
            border: "none",
            borderRadius: 5,
            cursor: "pointer",
          }}
        >
          Add Program
        </button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <select
          value={batchFilter}
          onChange={(e) => setBatchFilter(e.target.value)}
          style={{ ...fieldStyle, maxWidth: 320 }}
        >
          <option value="all">All batches</option>
          {batches.map((b) => (
            <option key={b._id} value={b._id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <CircularProgress />
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
          {items.length ? (
            items.map((item) => (
              <div
                key={item._id}
                style={{
                  width: 280,
                  padding: 16,
                  borderRadius: 8,
                  background: "#fff",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              >
                <h3 style={{ marginTop: 0 }}>{item.name}</h3>
                <p style={{ fontSize: 13, color: "#666", margin: "4px 0" }}>
                  Batch: {item.batch?.name || "—"}
                </p>
                <p style={{ fontSize: 13, color: "#666", margin: "4px 0" }}>
                  Courses: {item.coursesCount ?? 0} · Order: {item.order ?? 0}
                </p>
                <p style={{ fontSize: 13, margin: "4px 0" }}>
                  {item.isActive === false ? "Inactive" : "Active"}
                </p>
                <Actions
                  courseId={item._id}
                  onEdit={() => openEdit(item)}
                  onDelete={handleDelete}
                />
              </div>
            ))
          ) : (
            <p>No programs yet. Create a batch first, then add programs.</p>
          )}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        style={customModalStyles}
        contentLabel="Program"
      >
        <h2>{editingId ? "Edit Program" : "Add Program"}</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Batch</label>
            <select
              style={fieldStyle}
              value={form.batchId}
              onChange={(e) => setForm({ ...form, batchId: e.target.value })}
              required
            >
              <option value="" disabled>
                Select batch
              </option>
              {batches.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Name</label>
            <input
              style={fieldStyle}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Slug (optional)</label>
            <input
              style={fieldStyle}
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="auto from name"
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Description</label>
            <textarea
              style={{ ...fieldStyle, minHeight: 80 }}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Order</label>
            <input
              type="number"
              style={fieldStyle}
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) =>
                  setForm({ ...form, isActive: e.target.checked })
                }
              />
              Active
            </label>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4CAF50",
                color: "#fff",
                border: "none",
                borderRadius: 5,
                cursor: "pointer",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              style={{
                padding: "10px 20px",
                backgroundColor: "#ccc",
                border: "none",
                borderRadius: 5,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default SdcPrograms;
